import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import AuthModal from "./AuthModal";
import { useAuth } from "@/context/AuthContext";
import { scrollToSection } from "@/lib/scroll";
import { EASE } from "./Reveal";

const LINKS = [
    { label: "About", id: "about" },
    { label: "Features", id: "features" },
    { label: "How It Works", id: "how-it-works" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [authOpen, setAuthOpen] = useState(false);
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const onHome = location.pathname === "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        if (onHome) scrollToSection(id);
        else navigate("/", { state: { scrollTo: id } });
    };

    return (
        <>
            <header className="fixed inset-x-0 top-0 z-50 px-4 pt-4 sm:px-6">
                <motion.div
                    initial={{ y: -70, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
                    className={`mx-auto flex max-w-[1320px] items-center justify-between rounded-full border px-5 py-2.5 transition-[background-color,border-color,box-shadow] duration-500 sm:px-7 ${
                        scrolled
                            ? "border-slate-100 bg-white/80 shadow-[0_8px_30px_-14px_rgba(15,23,42,0.15)] backdrop-blur-xl"
                            : "border-white/40 bg-white/35 backdrop-blur-md"
                    }`}
                >
                    <Link
                        to="/"
                        data-testid="nav-home-link"
                        onClick={(e) => {
                            e.preventDefault();
                            if (onHome) scrollToSection("top");
                            else navigate("/");
                        }}
                    >
                        <Logo />
                    </Link>
                    <nav className="hidden items-center gap-9 md:flex" aria-label="Primary">
                        {LINKS.map((l) => (
                            <button
                                key={l.id}
                                onClick={() => go(l.id)}
                                className="nav-link"
                                data-testid={`nav-${l.id}-link`}
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        {user ? (
                            <Link
                                to="/account"
                                data-testid="nav-account-link"
                                className="hidden rounded-full border border-slate-200 px-4 py-2 text-sm font-light text-slate-700 transition-colors duration-300 hover:text-slate-950 sm:inline-flex"
                            >
                                {user.name || user.email}
                            </Link>
                        ) : (
                            <button
                                onClick={() => setAuthOpen(true)}
                                data-testid="nav-signin-button"
                                className="nav-link hidden sm:inline-flex"
                            >
                                Sign in
                            </button>
                        )}
                        <button
                            onClick={() => go("waitlist")}
                            data-testid="nav-waitlist-cta"
                            className="hidden rounded-full bg-slate-950 px-5 py-2.5 text-sm font-light tracking-wide text-white transition-colors duration-300 hover:bg-slate-800 active:scale-95 sm:inline-flex"
                        >
                            Join the Waitlist
                        </button>
                        <button
                            className="flex h-9 w-9 items-center justify-center rounded-full text-slate-950 md:hidden"
                            onClick={() => setOpen(true)}
                            aria-label="Open menu"
                            data-testid="nav-menu-button"
                        >
                            <Menu size={20} strokeWidth={1.5} />
                        </button>
                    </div>
                </motion.div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fixed inset-0 z-[60] bg-white/95 backdrop-blur-2xl"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        data-testid="nav-mobile-menu"
                    >
                        <div className="flex items-center justify-between px-6 pt-7">
                            <Logo />
                            <button
                                className="flex h-9 w-9 items-center justify-center rounded-full text-slate-950"
                                onClick={() => setOpen(false)}
                                aria-label="Close menu"
                                data-testid="nav-close-button"
                            >
                                <X size={22} strokeWidth={1.5} />
                            </button>
                        </div>
                        <nav className="mt-16 flex flex-col gap-8 px-8" aria-label="Mobile">
                            {LINKS.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    initial={{ y: 24, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.1 + i * 0.08, duration: 0.6, ease: EASE }}
                                    onClick={() => go(l.id)}
                                    className="text-left text-4xl font-extralight tracking-tight text-slate-950"
                                    data-testid={`nav-mobile-${l.id}-link`}
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                            <motion.button
                                initial={{ y: 24, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.32, duration: 0.6, ease: EASE }}
                                onClick={() => {
                                    setOpen(false);
                                    if (user) navigate("/account");
                                    else setAuthOpen(true);
                                }}
                                className="text-left text-4xl font-extralight tracking-tight text-slate-950"
                                data-testid="nav-mobile-auth-link"
                            >
                                {user ? "My account" : "Sign in"}
                            </motion.button>
                            <motion.button
                                initial={{ y: 24, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.38, duration: 0.6, ease: EASE }}
                                onClick={() => go("waitlist")}
                                className="mt-6 w-max rounded-full bg-slate-950 px-8 py-4 text-sm font-light tracking-wide text-white"
                                data-testid="nav-mobile-waitlist-cta"
                            >
                                Join the Waitlist
                            </motion.button>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
            <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
        </>
    );
}
